/**
 * Utility functions for popup window detection and management
 */

/**
 * Detects if the current window is running in a popup
 * @returns {boolean} True if the window is a popup, false otherwise
 */
export function isPopupWindow() {
    try {
        // Check if window has an opener (opened via window.open)
        if (window.opener && window.opener !== window) {
            return true
        }

        // Check if window is different from parent (iframe case)
        if (window.parent && window.parent !== window) {
            // This is an iframe, not a popup
            return false
        }

        // Additional checks for popup characteristics
        // Popups typically have limited features
        const hasLimitedFeatures =
            !window.menubar?.visible || !window.toolbar?.visible || !window.locationbar?.visible || !window.statusbar?.visible

        // Check if window was opened with specific dimensions (common for popups)
        const hasCustomDimensions = window.outerWidth < screen.availWidth || window.outerHeight < screen.availHeight

        return hasLimitedFeatures || hasCustomDimensions
    } catch (error) {
        console.warn('Error detecting popup window:', error)
        return false
    }
}

/**
 * Detects if the current window is running in an iframe
 * @returns {boolean} True if the window is in an iframe, false otherwise
 */
export function isIframeWindow() {
    try {
        return window.parent && window.parent !== window && !window.opener
    } catch (error) {
        console.warn('Error detecting iframe window:', error)
        return false
    }
}

/**
 * Gets the window context type
 * @returns {'popup'|'iframe'|'main'} The type of window context
 */
export function getWindowContext() {
    if (isPopupWindow()) {
        return 'popup'
    } else if (isIframeWindow()) {
        return 'iframe'
    } else {
        return 'main'
    }
}

/**
 * Sends a message to the parent/opener window
 * @param {Object} message - The message to send
 * @param {string} targetOrigin - The target origin (default: '*')
 * @returns {boolean} True if message was sent successfully, false otherwise
 */
export function sendMessageToParent(message, targetOrigin = '*') {
    try {
        const windowContext = getWindowContext()
        let messageSent = false

        if (windowContext === 'popup') {
            window.opener.postMessage(message, targetOrigin)
            messageSent = true
        } else if (windowContext === 'iframe' && window.parent) {
            window.parent.postMessage(message, targetOrigin)
            messageSent = true
        }

        return messageSent
    } catch (error) {
        console.error('Error sending message to parent:', error)
        return false
    }
}

/**
 * Attempts to close the current window
 * @param {Object} options - Options for closing
 * @param {number} options.delay - Delay before closing in milliseconds (default: 0)
 * @param {Object} options.fallbackMessage - Message to send to parent if window can't be closed
 * @returns {Promise<boolean>} Promise that resolves to true if window was closed, false otherwise
 */
export function closeWindow(options = {}) {
    const { delay = 0, fallbackMessage = { type: 'CLOSE_WINDOW_REQUEST' } } = options

    return new Promise(resolve => {
        const attemptClose = () => {
            const windowContext = getWindowContext()

            try {
                if (windowContext === 'popup') {
                    // For popups, try to close directly
                    window.close()

                    // Check if window actually closed
                    setTimeout(() => {
                        if (!window.closed) {
                            // If window didn't close, send message to opener
                            sendMessageToParent(fallbackMessage)
                            resolve(false)
                        } else {
                            resolve(true)
                        }
                    }, 100)
                } else if (windowContext === 'iframe') {
                    // For iframes, send close request to parent
                    const success = sendMessageToParent(fallbackMessage)
                    resolve(success)
                } else {
                    // For main windows, try to close anyway
                    window.close()
                    resolve(window.closed)
                }
            } catch (error) {
                console.error('Error closing window:', error)
                // Try to send message as fallback
                sendMessageToParent(fallbackMessage)
                resolve(false)
            }
        }

        if (delay > 0) {
            setTimeout(attemptClose, delay)
        } else {
            attemptClose()
        }
    })
}

/**
 * Adds event listeners for window communication
 * @param {Object} handlers - Event handlers
 * @param {Function} handlers.onMessage - Handler for postMessage events
 * @param {Function} handlers.onBeforeUnload - Handler for beforeunload events
 * @returns {Function} Cleanup function to remove event listeners
 */
export function addWindowEventListeners(handlers = {}) {
    const { onMessage, onBeforeUnload } = handlers

    const messageHandler = event => {
        if (onMessage) {
            onMessage(event)
        }
    }

    const beforeUnloadHandler = event => {
        if (onBeforeUnload) {
            onBeforeUnload(event)
        }
    }

    if (onMessage) {
        window.addEventListener('message', messageHandler)
    }

    if (onBeforeUnload) {
        window.addEventListener('beforeunload', beforeUnloadHandler)
    }

    // Return cleanup function
    return () => {
        if (onMessage) {
            window.removeEventListener('message', messageHandler)
        }
        if (onBeforeUnload) {
            window.removeEventListener('beforeunload', beforeUnloadHandler)
        }
    }
}

/**
 * Creates a comprehensive window info object
 * @returns {Object} Object containing window information
 */
export function getWindowInfo() {
    const context = getWindowContext()

    return {
        context,
        isPopup: context === 'popup',
        isIframe: context === 'iframe',
        isMain: context === 'main',
        hasOpener: !!window.opener,
        hasParent: window.parent !== window,
        canClose: context === 'popup' || (context === 'main' && window.opener),
        dimensions: {
            width: window.outerWidth,
            height: window.outerHeight,
            screenWidth: screen.availWidth,
            screenHeight: screen.availHeight,
        },
        features: {
            menubar: window.menubar?.visible,
            toolbar: window.toolbar?.visible,
            locationbar: window.locationbar?.visible,
            statusbar: window.statusbar?.visible,
        },
        url: window.location.href,
        origin: window.location.origin,
    }
}
